import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { seasonsAPI } from '../api/api';

// Farben der Zahlenzelle. Volle Sättigung mit weißer Schrift, damit die Zahl in
// Light- und Dark-Mode lesbar bleibt.
const statusStyles = {
  complete: 'bg-red-600 text-white',
  min_reached: 'bg-green-600 text-white',
  below_min: 'bg-orange-500 text-white',
  unknown: 'bg-gray-400 text-white'
};

const GamePeriods = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await seasonsAPI.getGamePeriods();
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="container mx-auto px-4 py-12 text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</div>;
  }

  if (error || !data) {
    return (
      <div className="container mx-auto px-4 py-12 text-center text-gray-500 dark:text-gray-400">
        {t('gamePeriods.noActiveSeason')}
      </div>
    );
  }

  const { season, currentPeriod, teams } = data;
  const counts = teams.reduce((acc, team) => {
    acc[team.status] = (acc[team.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">
          🗓️ {t('gamePeriods.title')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          {t('gamePeriods.subtitle', { season: season.name })}
        </p>
      </div>

      {currentPeriod ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">
                {currentPeriod.label}
                {currentPeriod.isActive && (
                  <span className="ml-2 text-xs font-semibold text-blue-600 dark:text-blue-400">
                    ({t('gamePeriods.running')})
                  </span>
                )}
                {currentPeriod.isUpcoming && (
                  <span className="ml-2 text-xs font-semibold text-yellow-600 dark:text-yellow-400">
                    ({t('gamePeriods.upcoming')})
                  </span>
                )}
                {currentPeriod.isPast && (
                  <span className="ml-2 text-xs font-semibold text-gray-400">
                    ({t('gamePeriods.finished')})
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {new Date(currentPeriod.startDate).toLocaleDateString()} – {new Date(currentPeriod.endDate).toLocaleDateString()}
                {currentPeriod.isActive && currentPeriod.daysLeft >= 0 && (
                  <> • {t('gamePeriods.daysLeft', { days: currentPeriod.daysLeft })}</>
                )}
              </p>
            </div>
            <div className="flex gap-6 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">{t('gamePeriods.minGames')}</p>
                <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{currentPeriod.minGames}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">{t('gamePeriods.maxGames')}</p>
                <p className="text-xl font-bold text-gray-800 dark:text-gray-200">
                  {currentPeriod.maxGames ?? '–'}
                </p>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">{t('gamePeriods.cumulativeHint')}</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-6 text-sm text-gray-500 dark:text-gray-400">
          {t('gamePeriods.noPeriods')}
        </div>
      )}

      {/* Legende */}
      <div className="flex flex-wrap gap-3 mb-6 text-sm">
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold ${statusStyles.below_min}`}>
          {counts.below_min || 0} × {t('gamePeriods.legend.belowMin')}
        </span>
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold ${statusStyles.min_reached}`}>
          {counts.min_reached || 0} × {t('gamePeriods.legend.minReached')}
        </span>
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold ${statusStyles.complete}`}>
          {counts.complete || 0} × {t('gamePeriods.legend.complete')}
        </span>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                {t('gamePeriods.cols.team')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                {t('gamePeriods.cols.gm')}
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                {t('gamePeriods.cols.gamesPlayed')}
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                {t('gamePeriods.cols.toMin')}
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                {t('gamePeriods.cols.toMax')}
              </th>
            </tr>
          </thead>
          <tbody>
            {teams.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                  {t('gamePeriods.noTeams')}
                </td>
              </tr>
            ) : (
              teams.map(team => (
                <tr key={team.teamId} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <Link to={`/teams/${team.teamId}`} className="flex items-center gap-3 font-medium text-gray-800 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400">
                      {team.logoUrl && <img src={team.logoUrl} alt="" className="w-7 h-7 object-contain" />}
                      <span>{team.city} {team.name}</span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{team.ownerName || '–'}</td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center justify-center min-w-[3rem] px-3 py-1.5 rounded-lg text-lg font-bold tabular-nums ${statusStyles[team.status] || statusStyles.unknown}`}
                      title={t(`gamePeriods.legend.${team.status === 'below_min' ? 'belowMin' : team.status === 'min_reached' ? 'minReached' : 'complete'}`)}
                    >
                      {team.gamesPlayed}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400 tabular-nums">
                    {team.gamesToMin > 0 ? team.gamesToMin : '✓'}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400 tabular-nums">
                    {team.gamesLeftToMax === null ? '–' : team.gamesLeftToMax}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GamePeriods;
